import { Entity } from '@api/api/entity-schema';
import { ExploreSingleInsightWithMeta } from '@api/api/insights';
import {
  ChartEmpty,
  ChartError,
  HorizonWithPriceRange,
  RangeHorizon,
  TimeSeriesItem,
} from '@chart';
import { ChartV2Horizon, ChartV2Insight } from '@chartv2';
import { ButtonGroup } from '@design/components/button-group/ButtonGroup';
import { Toggle } from '@design/components/toggle/Toggle';
import { groupBy } from '@helpers/utils/arrays/arrays';
import { getChartV2PaneId } from '@helpers/utils/chart-v2-utils/chart-v2-utils';
import { config } from '@terminal/config';
import { startOfDayUTC } from '@terminal/modules/impacted-companies/components/utils';
import { ChartAreaV2 } from '@terminal/modules/simple-chart/components/chart-area-v2/ChartAreaV2';
import { ChartLoading } from '@terminal/modules/simple-chart/components/chart-loading/ChartLoading';
import { getPriceHorizonSettings } from '@terminal/modules/simple-chart/utils/simple-chart-utils';
import {
  terminalPanesToDefaultPriceDisplayByPane,
  terminalPanesToSeries,
  toChartV2Domain,
  toChartV2Horizon,
} from '@terminal/views/chart-v2/utils/map-terminal-chart';
import { useExploreStore } from '@terminal/views/explore/hooks/use-explore-store/useExploreStore';
import { ChartState } from '@terminal/views/turbo-chart/hooks/use-chart-state/useChartState';
import { useChartTheme } from '@terminal/views/turbo-chart/hooks/use-chart-theme/useChartTheme';
import {
  TerminalChartAssetData,
  TerminalPaneData,
} from '@terminal/views/turbo-chart/utils/chart-utils/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PriceInsightMarker } from '../price-insight-marker/PriceInsightMarker';
import styles from './PriceChart.module.scss';

export const HORIZONS = [
  RangeHorizon.OneWeek,
  RangeHorizon.TwoWeeks,
  RangeHorizon.OneMonth,
  RangeHorizon.TwoMonths,
  RangeHorizon.ThreeMonths,
  RangeHorizon.SixMonths,
];

const AREA_GRADIENT = [
  {
    stop: 0,
    opacity: 0.5,
  },
  {
    stop: 1,
    opacity: 0.1,
  },
];

export interface PriceChartProps {
  chartState: ChartState;
  chartData: TerminalChartAssetData[];
  dailyTimeseries?: TimeSeriesItem[];
  horizonRanges: RangeHorizon[];
  hasError: boolean;
  hasEmpty: boolean;
  assetWithError?: Entity;
  primaryAsset?: TerminalChartAssetData;
  primaryPane?: TerminalPaneData;
  activeInsightId: string;
  selectedInsights: ExploreSingleInsightWithMeta[];
  filteredOnly: boolean;
  onHorizonChange: (horizon: RangeHorizon) => void;
  setFilteredOnly: (isFilteredOnly: boolean) => void;
  handleSetActiveInsightId: (id: string) => void;
}

export const PriceChart = ({
  chartState,
  chartData,
  dailyTimeseries,
  horizonRanges,
  hasError,
  hasEmpty,
  assetWithError,
  primaryAsset,
  primaryPane,
  activeInsightId = '',
  selectedInsights,
  filteredOnly,
  onHorizonChange,
  setFilteredOnly,
  handleSetActiveInsightId,
}: PriceChartProps) => {
  const { t } = useTranslation(['widget', 'insights', 'chart']);

  const [horizonWithPriceRanges, setHorizonWithPriceRanges] = useState<
    HorizonWithPriceRange[]
  >([]);
  const handleSetActiveInsightIdRef = useRef(handleSetActiveInsightId);
  handleSetActiveInsightIdRef.current = handleSetActiveInsightId;

  const { colors: themeColors } = useChartTheme();
  const activeHorizonIdx = useMemo(() => {
    return HORIZONS.findIndex(horizon => horizon === chartState.horizon);
  }, [chartState.horizon]);
  const { getShouldShowToggleFiltered } = useExploreStore(state => ({
    getShouldShowToggleFiltered: state.getShouldShowToggleFiltered,
  }));
  const primaryLineColorToken = chartData[0]?.lineColorToken;
  const chartV2Series = useMemo(
    () =>
      terminalPanesToSeries(chartState.chartPanes, {
        shouldUseLineColorToken: true,
        areaGradient: AREA_GRADIENT,
        showLatestPrice: false,
      }).map(series => ({ ...series, yAxisLabel: '' })),
    [chartState.chartPanes, primaryLineColorToken]
  );
  const chartV2Insights = useMemo<ChartV2Insight[]>(() => {
    const insightsByDate = groupBy(selectedInsights, insight =>
      startOfDayUTC(new Date(insight.created_at)).toISOString()
    );

    return Object.entries(insightsByDate).map(([date, groupedInsights]) => ({
      id: `price-insight-${date}`,
      time: startOfDayUTC(new Date(date)).getTime(),
      paneId: getChartV2PaneId(0),
      icons: groupedInsights.map(insight => (
        <PriceInsightMarker
          key={insight.id}
          insight={insight}
          active={insight.id === activeInsightId}
          onClick={id => handleSetActiveInsightIdRef.current(id)}
        />
      )),
    }));
  }, [activeInsightId, selectedInsights]);

  useEffect(() => {
    if (dailyTimeseries && chartData[0]) {
      const { rangesWithPrice, lineColorToken } = getPriceHorizonSettings({
        horizonRanges,
        dailyTimeseries,
        horizon: HORIZONS[activeHorizonIdx],
        colors: themeColors,
      });

      if (lineColorToken) {
        chartData[0].lineColorToken = lineColorToken;
      }

      setHorizonWithPriceRanges(rangesWithPrice);
    }
  }, [dailyTimeseries]);

  const horizonsLabels = HORIZONS.map(horizon =>
    /* i18next-extract-mark-context-next-line ["OneWeek", "TwoWeeks", "OneMonth", "TwoMonths", "ThreeMonths", "SixMonths"] */
    t('widget:insights.insightHorizon', { context: horizon })
  );

  const handleHorizonChange = (horizon: RangeHorizon) => {
    if (chartData.length) {
      const found = horizonWithPriceRanges.find(h => h.horizon === horizon);

      if (found) {
        chartData[0].lineColorToken = found.lineColorToken;
      }
    }

    onHorizonChange(horizon);
  };

  const availableHorizons = HORIZONS.map(toChartV2Horizon).filter(
    (horizon): horizon is ChartV2Horizon => Boolean(horizon)
  );
  const emptyLocales = {
    imgAlt: t('chart:empty.imgAlt'),
    title: t('chart:empty.title', { asset: assetWithError?.name }),
    description: t('chart:empty.description'),
    btnLabel: t('chart:empty.btnLabel'),
  };
  const errorLocales = {
    imgAlt: t('chart:error.imgAlt'),
    title: t('chart:error.title'),
    description: t('chart:error.description'),
    linkAlt: t('chart:error.linkAlt'),
  };
  const isLoading = !hasError && !hasEmpty && !dailyTimeseries && !chartData[0];

  if (isLoading) {
    return (
      <ChartLoading
        className={styles.chartLoading}
        isLegacyVariant
        isChatMessage={false}
      />
    );
  }

  const showToggle = getShouldShowToggleFiltered();

  if (!primaryAsset || !primaryPane) {
    return null;
  }

  return (
    <>
      <div className={styles.wrapper}>
        <ButtonGroup
          className={styles.buttonGroup}
          labels={horizonsLabels}
          onClick={index => handleHorizonChange(HORIZONS[index])}
          activeIndex={activeHorizonIdx}
        />
        {showToggle && (
          <Toggle
            rightLabel={t('insights:toggleFiltered')}
            isChecked={filteredOnly}
            onToggle={setFilteredOnly}
          />
        )}
      </div>
      <div className={styles.chartAreaWrapper} data-clipboard-snapshot>
        {hasError && (
          <ChartError
            supportEmail={config.supportEmail}
            locales={errorLocales}
          />
        )}
        {((!hasError && assetWithError) || hasEmpty) && (
          <ChartEmpty locales={emptyLocales} />
        )}
        {!!chartData.length && !hasEmpty && (
          <ChartAreaV2
            series={chartV2Series}
            chartData={chartData}
            primaryPane={primaryPane}
            horizon={chartState.horizon}
            resample={chartState.resample}
            availableHorizons={availableHorizons}
            disable1DHorizon={false}
            enableZoom={false}
            enableHover
            isLegacyVariant
            insights={chartV2Insights}
            onHorizonChange={handleHorizonChange}
            domain={toChartV2Domain(chartState.domain, primaryAsset)}
            defaultPriceDisplayByPane={terminalPanesToDefaultPriceDisplayByPane(
              chartState.chartPanes
            )}
          />
        )}
      </div>
    </>
  );
};
